import { ExportUsersDialog, UserFilters } from "@/components/users";
import { PermissionGate } from "@/components/common/PermissionGate";
import { UserTable } from "@/components/users/UserTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import { useExportUsers, useUsers } from "@/hooks/useUserQueries";
import { useUserFiltersStore } from "@/stores/userFiltersStore";
import { USER_COLUMNS, useUserColumnsStore } from "@/stores/userColumnsStore";
import { Download, RefreshCw, Users2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { MAX_EXPORT_SELECTION, type ExportUsersRequest } from "@/types/admin";

// ============================================================================
// Helpers
// ============================================================================

/**
 * Builds a page-number list with "ellipsis" sentinels for the pagination bar.
 * Always includes page 1 and the last page; collapses middle runs with "…".
 */
function getPaginationItems(
  currentPage: number,
  totalPages: number,
): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items: (number | "ellipsis")[] = [1];
  if (currentPage > 3) items.push("ellipsis");
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);
  for (let i = start; i <= end; i++) items.push(i);
  if (currentPage < totalPages - 2) items.push("ellipsis");
  items.push(totalPages);
  return items;
}

// ============================================================================
// Component
// ============================================================================

export function UserListPage() {
  // isLoading only covers the very first load — the one render pass with no rows to show yet.
  // Every later fetch keeps the previous page on screen (`placeholderData` on the hook), so
  // isFetching is what says "still arriving" and drives the pill anchored to the Card below.
  const filters = useUserFiltersStore((state) => state.filters);
  const page = useUserFiltersStore((state) => state.page);
  const pageSize = useUserFiltersStore((state) => state.pageSize);
  const setPage = useUserFiltersStore((state) => state.setPage);
  const setPageSize = useUserFiltersStore((state) => state.setPageSize);
  const clearCache = useUserFiltersStore((state) => state.clearCache);
  const hasActiveFilters = useUserFiltersStore((state) =>
    state.hasActiveFilters(),
  );
  // Column visibility lives in its own store so the menu here and the table below share it without
  // props. Subscribing to `hidden` (the value that changes) and deriving from it — see the same
  // note in UserTable.
  const hiddenColumns = useUserColumnsStore((state) => state.hidden);
  const toggleColumn = useUserColumnsStore((state) => state.toggle);
  const showAllColumns = useUserColumnsStore((state) => state.showAll);

  const {
    data: response,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useUsers({ ...filters, page, pageSize });

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  const exportUsers = useExportUsers();

  const users = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = response?.pagination?.currentPage || 1;
  const totalItems = response?.pagination?.totalItems || 0;

  // The export endpoint refuses more than MAX_EXPORT_SELECTION ids. Stopping here turns that into
  // a clear message at the moment the operator crosses the line, instead of a raw validation
  // error after they have already ticked their way past it.
  const warnSelectionLimit = () =>
    toast.warning(
      `You can select at most ${MAX_EXPORT_SELECTION} users for one export.`,
    );

  // Decided from the rendered `selectedIds` rather than inside a setState updater: StrictMode
  // invokes updaters twice, which would fire the toast twice for one click.
  const toggleSelect = (userId: string) => {
    if (selectedIds.includes(userId)) {
      setSelectedIds(selectedIds.filter((id) => id !== userId));
      return;
    }

    if (selectedIds.length >= MAX_EXPORT_SELECTION) {
      warnSelectionLimit();
      return;
    }

    setSelectedIds([...selectedIds, userId]);
  };

  const toggleSelectPage = (select: boolean) => {
    const pageIds = users.map((user) => user.id);

    if (!select) {
      // Only the current page is cleared: selections made on other pages are still live and
      // still counted in the export, so dropping them here would silently lose them.
      setSelectedIds(selectedIds.filter((id) => !pageIds.includes(id)));
      return;
    }

    const merged = Array.from(new Set([...selectedIds, ...pageIds]));

    if (merged.length > MAX_EXPORT_SELECTION) {
      warnSelectionLimit();
      setSelectedIds(merged.slice(0, MAX_EXPORT_SELECTION));
      return;
    }

    setSelectedIds(merged);
  };

  const handleExportClick = () => {
    if (selectedIds.length > 0) {
      // A selection is its own filter — export exactly those rows and skip the dialog.
      exportUsers.mutate({ userIds: selectedIds });
      return;
    }

    setExportDialogOpen(true);
  };

  const handleFilteredExport = (payload: ExportUsersRequest) => {
    exportUsers.mutate(payload, {
      onSuccess: () => setExportDialogOpen(false),
    });
  };

  // Refresh is the screen's "start over": it drops every filter, the page size and the cached copy
  // in sessionStorage, then reloads. UserFilters holds its own copy of the half-typed term and
  // mirrors the store, so clearing the store is enough to clear the input too.
  const handleRefresh = () => {
    clearCache();
    // The ticked rows are part of "everything" too. They are kept across pages and filter changes
    // on purpose — the export sends explicit ids, so a selection built over several pages is the
    // feature — but a reset that throws the filters away and leaves 40 users ticked would export a
    // set the operator chose against filters that no longer exist and can no longer be seen.
    setSelectedIds([]);
    void refetch();
  };

  // Only take the screen over when there is genuinely nothing to show. `useUsers` keeps the
  // previous page on screen through `placeholderData`, so a refetch that fails — a token refresh
  // losing a race, a blip on one filter change — used to replace a working table AND the whole
  // filter bar with a "Retry" card. The operator then had to set their filters up again to see rows
  // that were still in the cache a moment earlier. With rows in hand the failure is reported in a
  // banner above the table instead, and Retry is one click away either way.
  if (error && !response) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load users</p>
        <Button variant="outline" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      {/* Table Card */}
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {/* Anchored to the card, not the viewport: `fixed inset-0` would centre this on the screen,
            so it would hover in whatever blank space happened to be mid-window rather than over the
            rows it describes. Same shape as the payments and resources lists.

            It replaces the 60% dimming this screen used to apply while `isPlaceholderData` was
            true. The two said different things — "a request is in flight" against "the rows you are
            reading belong to the previous filter" — but they were on screen together for almost
            every filter change, and one of them greyed out a table the operator was still reading.
            The pill is the signal the other two admin lists use, so all three now say it the same
            way. */}
        {isFetching && !isLoading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-3">
            <div className="min-w-0 flex-1">
              {/* Export sits in the slot right after the search box, not at the far end of the
                  toolbar: it is the only control here that produces something, and the shared
                  list-screen order puts the screen's own action next to the input. */}
              <UserFilters
                actions={
                  <PermissionGate roles={["SuperAdmin", "SupportAdmin"]}>
                    <Button
                      onClick={handleExportClick}
                      variant="outline"
                      size="sm"
                      disabled={exportUsers.isPending}
                      className="h-8 min-h-8 shrink-0 gap-2 px-2 text-sm hover:translate-y-0 active:translate-y-0"
                    >
                      <Download className="h-4 w-4" />
                      {exportUsers.isPending ? "Exporting..." : "Export Excel"}
                    </Button>
                  </PermissionGate>
                }
              />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <ColumnSettingsMenu
                columns={USER_COLUMNS}
                isVisible={(id) => !hiddenColumns.includes(id)}
                toggle={toggleColumn}
                showAll={showAllColumns}
                hiddenCount={hiddenColumns.length}
                className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
              />
              {selectedIds.length > 0 && (
                <Button
                  onClick={() => setSelectedIds([])}
                  variant="ghost"
                  size="sm"
                  className="h-8 min-h-8 gap-1 px-2 text-sm text-muted-foreground hover:translate-y-0 active:translate-y-0"
                >
                  <X className="h-4 w-4" />
                  {selectedIds.length} selected
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent
          aria-busy={isFetching}
          className="flex min-h-0 flex-1 flex-col p-2 pt-0"
        >
          {/* Reached only with a previous result in hand — the no-data case returned above. A failed
              background refetch says so here and leaves the table and the filter bar alone. */}
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          {isLoading ? (
            /* The first load has no rows yet, and the empty state below reads an empty list as an
               answer: an operator opening the screen was told there was nothing here before the
               request had come back. UserTable renders skeleton rows for this. */
            <UserTable
              users={users}
              isLoading
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectPage={toggleSelectPage}
            />
          ) : users.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              {/* Fills the card the way the table does instead of collapsing to its own
                  content. CardContent is a min-h-0 flex column, so a fixed-height block here
                  left the card ending part-way down the screen: the whole panel changed size
                  the moment a filter emptied the list, and changed back when it matched
                  again. justify-center then puts the message in the middle of the space the
                  rows occupied rather than tucked under the toolbar. */}
              <Users2 className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No users found" : "No users yet"}
              </h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting or clearing your filters."
                  : "Users will appear here once created."}
              </p>
            </div>
          ) : (
            <UserTable
              users={users}
              startIndex={(currentPage - 1) * pageSize}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onToggleSelectPage={toggleSelectPage}
            />
          )}

          {/* The footer sits outside the three branches above, so a search that matched nothing
              still shows its pager, its page-size picker and its total. It reads "Page 1 of 1
              (0 total)" with both arrows disabled — a true statement about a result that came back,
              and the row an operator needs to change the page size or step back off an empty page.

              `!isLoading` is the one case still held back: during the very first load "0 total"
              would describe a request that has not answered yet, which is the same lie the skeleton
              above exists to avoid. */}
          {!isLoading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages} ({totalItems} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                {/* Rendered at every page count, not just `totalPages > 1`: hiding the pager on a
                    single page made the controls appear and disappear as filters changed, and left
                    a zero-result search with no way to change the page size. Previous and Next
                    disable themselves at the ends, so a single page simply shows both greyed out
                    next to page 1. */}
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(currentPage, totalPages).map(
                    (item, index) =>
                      item === "ellipsis" ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="px-1 text-sm text-muted-foreground"
                        >
                          …
                        </span>
                      ) : (
                        <Button
                          key={item}
                          onClick={() => setPage(item)}
                          disabled={isFetching}
                          variant={currentPage === item ? "default" : "outline"}
                          size="sm"
                          className="min-w-9"
                        >
                          {item}
                        </Button>
                      ),
                  )}
                  <Button
                    onClick={() =>
                      setPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                {/* Moved down from the toolbar to sit with the other paging controls, the same
                    place every other admin list keeps it. Icon only, and the tooltip says what the
                    click actually does: handleRefresh is this screen's "start over" - it drops
                    every filter, the cached copy in sessionStorage and the row selection before
                    reloading - so a caption reading "Refresh" would promise a reload while quietly
                    emptying the filter bar. */}
                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ExportUsersDialog
        open={isExportDialogOpen}
        onOpenChange={setExportDialogOpen}
        onExport={handleFilteredExport}
        isExporting={exportUsers.isPending}
      />
    </div>
  );
}
